"use client";

import { cn } from "@/lib/utils";
import { CONTROL_PRESS } from "./surface-styles";

const SELECTED_TONE = {
  ok: "bg-nf-ds-tone-ok-bg text-nf-ds-tone-ok-fg",
  warn: "bg-nf-ds-tone-warn-bg text-nf-ds-tone-warn-fg",
  err: "bg-nf-ds-tone-err-bg text-nf-ds-tone-err-fg",
} as const;

/**
 * A two-or-three-way choice, shown as one control.
 *
 * Used where a select would technically work but hides the alternatives.
 * Daily vs Lifetime changes what the fields below it mean, and so does
 * "Needs you" against "All" on a video gallery: both options being visible is
 * worth the width, because a closed select makes the reader open it to learn
 * there was a second way to look.
 *
 * Radios rather than buttons: one choice, N answers, and arrow keys move
 * within the group for free.
 */
export function Segmented<Id extends string>({
  name,
  value,
  onChange,
  options,
  legend,
  fillOnPhone = false,
}: {
  /** Distinct per group, or two segmented controls on one page share state. */
  name: string;
  value: Id;
  onChange: (next: Id) => void;
  options: readonly {
    id: Id;
    name: string;
    /**
     * A small mark beside the label: this segment has something in it.
     *
     * The sanctioned place for a dot. `GUIDELINES.md` keeps dots out of
     * badges because a pill is already an indicator, and points them here
     * instead: a dot beside plain text, where nothing else is signalling.
     */
    dot?: boolean;
    /** What the dot means, for a reader who cannot see it. */
    dotLabel?: string;
    /**
     * Fill the segment with a status tone when selected, instead of brand.
     * For a choice whose options are themselves statuses (a client wants
     * Spanish / is not keen / unknown), where the colour is the answer.
     */
    tone?: "ok" | "warn" | "err";
  }[];
  /** Announced for the group; visually the caller's own label does the work. */
  legend: string;
  /**
   * Fill the width and grow the targets, below `lg`.
   *
   * Opt-in rather than the default, because the answer depends on what the
   * control sits in: in a toolbar or a modal a segmented control that spans
   * the sheet looks like a tab bar it is not. On a page where it is the only
   * control in its row it should span, and then each segment is a third of
   * the screen instead of a 60-point word, which is the difference between
   * aiming and tapping.
   *
   * The height is the DS control height rather than the portal's 48pt touch
   * minimum. That minimum is written for a lone glyph or a short row, where
   * height is the only dimension a thumb gets; a segment that already spans
   * a third of the screen has the area several times over, and stacking 48
   * on top of that made three buttons that looked like a menu rather than a
   * filter. Width is doing the work here, so height does not have to.
   */
  fillOnPhone?: boolean;
}) {
  return (
    <fieldset>
      <legend className="sr-only">{legend}</legend>
      <div
        className={cn(
          "inline-flex gap-nf-ds-tight rounded-[14px] bg-nf-ds-surface-ground p-[3px] ring-1 ring-nf-ds-surface-card-ring ring-inset",
          fillOnPhone && "max-lg:flex max-lg:w-full",
        )}
      >
        {options.map((option) => {
          const selected = option.id === value;
          return (
            <label
              key={option.id}
              className={cn(
                // `relative` for the dot, which hangs off the corner rather
                // than sitting in the text flow.
                "relative inline-flex cursor-pointer items-center rounded-[11px] px-nf-ds-block py-[7px] font-nf-ds-sans text-[13px] font-[500] leading-none outline-hidden",
                fillOnPhone &&
                  "max-lg:min-h-nf-ds-control max-lg:flex-1 max-lg:justify-center",
                "transition-[background-color,color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none",
                "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-nf-ds-brand",
                CONTROL_PRESS,
                selected
                  ? option.tone
                    ? SELECTED_TONE[option.tone]
                    : "bg-nf-ds-brand text-nf-ds-white"
                  : "text-muted-foreground hover:text-nf-ds-fg",
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.id}
                checked={selected}
                onChange={() => onChange(option.id)}
                className="sr-only"
              />
              {option.name}
              {option.dot ? (
                <>
                  {/*
                    Off the top-right corner, not beside the word.

                    In the text flow it read as punctuation the label had
                    grown; a notification mark belongs where the eye already
                    looks for one, which is the corner of the thing it is
                    about. It sits inside the segment's own 24 points of
                    horizontal padding, so it never crowds the label however
                    long that label is.

                    Warn rather than brand: the colour the rest of the
                    product already uses for "this one is waiting on you",
                    and it stays legible both on the ground and on the
                    selected segment's brand fill.
                  */}
                  <span
                    aria-hidden="true"
                    className="absolute top-[6px] right-[8px] size-1.5 rounded-full bg-nf-ds-tone-warn-fg"
                  />
                  {option.dotLabel ? (
                    <span className="sr-only">{option.dotLabel}</span>
                  ) : null}
                </>
              ) : null}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
